import React from "react";
import { motion } from "framer-motion";

interface Service {
  name: string;
  category: string;
  description: string;
  members: string;
  icon: string;
  comingSoon?: boolean;
}

const services: Service[] = [
  {
    name: "Member Onboarding",
    category: "Getting Started",
    description: "Seamless welcome experience with personalized introductions to community resources and connections.",
    members: "2.5k helped",
    icon: "👋",
    comingSoon: true,
  },
  {
    name: "Event Coordination",
    category: "Community Events",
    description: "Discover upcoming community gatherings that enrich your work experience.",
    members: "8.2k assisted",
    icon: "🎉",
  },
  {
    name: "Resource Navigator",
    category: "Knowledge Base",
    description: "Instantly find guides, FAQs, and community resources tailored to your needs.",
    members: "15.3k queries",
    icon: "📚",
  },
  {
    name: "Connection Matchmaker",
    category: "Networking",
    description: "Get introduced to like-minded members based on shared interests and goals.",
    members: "4.1k matches",
    icon: "🔗",
    comingSoon: true,
  },
  {
    name: "Booking Assistant",
    category: "Reservations",
    description: "Book conference rooms, amenities, and workspace resources with ease.",
    members: "3.2k bookings",
    icon: "📆",
  },
  {
    name: "Support Assistant",
    category: "Help Desk",
    description: "Get instant answers to common questions and escalate complex issues when needed.",
    members: "12.7k resolved",
    icon: "🛟",
  },
];

const FeaturedRepos = () => {
  return (
    <section id="services" className="py-24 px-6">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-12 text-center">
          How Can I Help You Today?
        </h2>
        
        <motion.div 
          className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.1,
                delayChildren: 0.1
              }
            }
          }}
        >
          {services.map((service, index) => (
            <ServiceCard key={service.name} service={service} index={index} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const cardAnimations = [
  { x: -40, y: -30, rotate: -8 },
  { x: 0, y: -40, rotate: 5 },
  { x: 40, y: -30, rotate: 8 },
  { x: -35, y: 35, rotate: 6 },
  { x: 0, y: 40, rotate: -5 },
  { x: 35, y: 35, rotate: -8 },
];

const ServiceCard = ({ service, index }: { service: Service; index: number }) => {
  const animation = cardAnimations[index % cardAnimations.length];

  return (
    <motion.div
      className={`landing-glass rounded-xl p-6 landing-card-hover block cursor-pointer will-change-transform ${service.comingSoon ? 'landing-coming-soon-wrapper' : ''}`}
      variants={{
        hidden: {
          opacity: 0,
          x: animation.x,
          y: animation.y,
          rotate: animation.rotate,
          scale: 0.9
        },
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
          rotate: 0,
          scale: 1,
          transition: {
            type: "spring",
            stiffness: 120,
            damping: 14,
            mass: 0.8
          }
        }
      }}
    >
      {service.comingSoon && <div className="landing-coming-soon-ribbon" />}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-xl">
          {service.icon}
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{service.name}</h3>
          <p className="text-sm text-muted-foreground">{service.category}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
        {service.description}
      </p>

      {/* <div className="flex items-center gap-1 text-muted-foreground">
        <Users className="w-4 h-4 text-primary" />
        <span className="text-sm">{service.members}</span>
      </div> */}
    </motion.div>
  );
};

export default FeaturedRepos;
